/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Script } from "@packages/framework";
import type { MediaElementsProps, MediaElementsRef } from "@packages/media";
import { MediaElementContext } from "@packages/media";
import { uuid } from "@packages/sdk";
import type HLS from "hls.js";
import type { RefObject, SyntheticEvent } from "react";
import {
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  numericPercentages,
  numericViewportWidth,
} from "../../../../../../global/emotion";
import { HallowImage } from "../../../../../components/blocks/other/HallowImage/HallowImage";
import type { HallowAudioElement } from "../../../primitives/Audio/Audio";
import { Audio } from "../../../primitives/Audio/Audio";
import type { HallowVideoElement } from "../../../primitives/Video/Video";
import { Video } from "../../../primitives/Video/Video";

const imageStyle: Interpolation = {
  height: numericPercentages[100],
  objectFit: "cover",
  objectPosition: "center center",
  width: numericPercentages[100],
};
const videoStyle: Interpolation = {
  height: numericPercentages[100],
  maxWidth: numericViewportWidth[100],
  objectFit: "contain",
  objectPosition: "center center",
  width: "auto",
};

export type HallowMediaElement = HallowVideoElement | HallowAudioElement;

export const MediaElements = forwardRef<MediaElementsRef, MediaElementsProps>(
  (
    {
      imgSrc,
      mediaSrc,
      mediaType,
      mediaTitle,
      bgAudioSrc,
      bgVolume = 0.25,
      shouldPlayBg,
    },
    outsideRef,
  ) => {
    const ref = useRef<HallowMediaElement | null>(null);
    const background = useRef<HallowAudioElement>(null);
    const bgPromise = useRef<Promise<void>>(Promise.resolve());
    const fgPromise = useRef<Promise<void>>(Promise.resolve());
    const player = useContext(MediaElementContext);
    const [resetUuid, setResetUuid] = useState<string>(uuid());
    const shouldPlayBgRef = useRef<boolean>(shouldPlayBg);

    useEffect(() => {
      shouldPlayBgRef.current = shouldPlayBg;
    }, [shouldPlayBg]);

    const hlsUrl = mediaSrc?.[0]?.url ?? null;
    const policyCookie =
      mediaSrc?.[0]?.cookies?.[0]?.["CloudFront-Policy"] ?? null;
    const keyPairIdCookie =
      mediaSrc?.[0]?.cookies?.[0]?.["CloudFront-Key-Pair-Id"] ?? null;
    const signatureCookie =
      mediaSrc?.[0]?.cookies?.[0]?.["CloudFront-Signature"] ?? null;
    const regularUrl = mediaSrc?.[1] ?? null;
    const bgHlsUrl = bgAudioSrc?.[0]?.url ?? null;
    const bgPolicyCookie =
      bgAudioSrc?.[0]?.cookies?.[0]?.["CloudFront-Policy"] ?? null;
    const bgKeyPairIdCookie =
      bgAudioSrc?.[0]?.cookies?.[0]?.["CloudFront-Key-Pair-Id"] ?? null;
    const bgSignatureCookie =
      bgAudioSrc?.[0]?.cookies?.[0]?.["CloudFront-Signature"] ?? null;
    const bgRegularUrl = bgAudioSrc?.[1] ?? null;

    /**
     * Allows us to programmatically change the currentTime on the <audio> or <video> element. Without this, we would
     * see the scrubber jump back and forth when controlled by the user, as the audio keeps playing in the background
     * and updating the time state (before the user's control changes it back visually). With this, we can trust a
     * change to be completed before the scrubber resumes updating based on playback.
     */
    const suspendUpdatesUntilTime = useRef<number>(-1);

    useEffect(() => {
      if (background.current?.element)
        background.current.element.volume =
          bgVolume * (ref.current?.element?.volume ?? 1); // default background volume
    }, [background.current?.element]);

    const handleNativeFullscreenExit = () => {
      if (
        // @ts-ignore
        !ref.current?.element?.webkitDisplayingFullscreen &&
        ref.current?.element?.textTracks
      ) {
        for (let t = 0; t < ref.current.element.textTracks.length; t++) {
          if (ref.current.element.textTracks[t].mode === "showing") {
            ref.current.element.textTracks[t].mode = "hidden";
          }
        }
        ref.current.element.removeEventListener(
          "webkitfullscreenchange",
          handleNativeFullscreenExit,
        );
      }
    };

    useImperativeHandle(outsideRef, () => ({
      set bgVolume(newVolume: number) {
        if (isFinite(newVolume) && background.current?.element)
          background.current.element.volume = newVolume * this.volume;
        else throw `Non-finite background volume requested: ${newVolume}`;
      },
      set currentTime(newTime: number) {
        if (ref.current?.element) {
          suspendUpdatesUntilTime.current = newTime;
          ref.current.element.currentTime = newTime;
        }
      },
      get currentTime() {
        return ref.current?.element?.currentTime ?? 0;
      },
      get duration(): number {
        return ref.current?.element?.duration ?? 0;
      },
      nativeFullscreen: () => {
        if (ref.current?.element?.textTracks) {
          for (let t = 0; t < ref.current.element.textTracks.length; t++) {
            if (ref.current.element.textTracks[t].mode === "hidden") {
              ref.current.element.textTracks[t].mode = "showing";
            }
          }
          ref.current.element.addEventListener(
            "webkitfullscreenchange",
            handleNativeFullscreenExit,
          );
        }
        // @ts-ignore
        return ref.current?.element?.webkitEnterFullscreen?.();
      },
      get isNativeFullscreen() {
        // @ts-ignore
        return !!ref.current?.element?.webkitDisplayingFullscreen;
      },
      pause: () => {
        if (background.current?.element?.src?.length) {
          bgPromise.current.finally(() => background.current?.element?.pause());
        }
        fgPromise.current
          .catch(player.onError)
          .finally(() => ref.current?.element?.pause());
      },
      pauseBackground: () => {
        bgPromise.current.finally(() => background.current?.element?.pause());
      },
      get paused(): boolean {
        return ref.current?.element?.paused ?? true;
      },
      play: async () => {
        if (
          (background.current?.element?.src?.length ||
            background.current?.element?.currentSrc?.length) &&
          shouldPlayBgRef.current
        ) {
          bgPromise.current.finally(
            () =>
              (bgPromise.current =
                background.current?.element?.play()?.catch(player.onBgError) ??
                Promise.resolve()),
          );
        }
        await fgPromise.current;
        fgPromise.current =
          ref.current?.element?.play()?.catch(player.onError) ??
          Promise.resolve();
        return fgPromise.current;
      },
      playBackground: async () => {
        if (
          (background.current?.element?.src?.length ||
            background.current?.element?.currentSrc?.length) &&
          shouldPlayBgRef.current
        ) {
          return bgPromise.current.finally(
            () =>
              (bgPromise.current =
                background.current?.element?.play()?.catch(player.onBgError) ??
                Promise.resolve()),
          );
        }
        return Promise.resolve();
      },
      set playbackRate(newSpeed: number) {
        if (isFinite(newSpeed) && ref.current?.element)
          ref.current.element.playbackRate = newSpeed;
        else throw `Non-finite playback rate requested: ${newSpeed}`;
      },
      get playbackRate(): number {
        return ref.current?.element?.playbackRate ?? 1;
      },
      set volume(newVolume: number) {
        if (newVolume !== undefined) {
          if (isFinite(newVolume) && ref.current?.element) {
            ref.current.element.volume = newVolume;
            if (background.current?.element)
              background.current.element.volume = bgVolume * newVolume;
          } else throw `Non-finite volume requested: ${newVolume}`;
        }
      },
      get volume(): number {
        return ref.current?.element?.volume ?? 1;
      },
      reset() {
        setTimeout(() => setResetUuid(uuid()), 10);
      },
      get hls(): HLS {
        return ref.current.hls;
      },
      get textTracks(): TextTrackList | null {
        return ref.current?.element?.textTracks ?? null;
      },
      get width(): number | null {
        return ref.current?.element?.clientWidth ?? null;
      },
    }));

    const handlePlay = useCallback(() => player?.updatePaused(false), [player]);
    const handlePause = useCallback(() => player?.updatePaused(true), [player]);
    const handleTimeUpdate = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) => {
        if (
          suspendUpdatesUntilTime.current > 0 &&
          Math.abs(
            suspendUpdatesUntilTime.current - e.currentTarget.currentTime,
          ) > 0.5
        )
          return;
        player?.updateTime(e.currentTarget.currentTime);
        if (suspendUpdatesUntilTime.current > 0)
          suspendUpdatesUntilTime.current = -1;
      },
      [player],
    );
    const handleSpeedChange = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) =>
        player?.updateSpeed(e.currentTarget.playbackRate),
      [player],
    );
    const handleVolumeChange = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) =>
        player?.updateVolume(e.currentTarget.volume),
      [player],
    );
    const handleLoad = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) => {
        // Safari doesn't populate src with HLS
        if (e.currentTarget.src || e.currentTarget.currentSrc) {
          player?.onLoaded();
        }
      },
      [player],
    );
    const handleMetadataLoad = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) => {
        if (e.currentTarget.src || e.currentTarget.currentSrc) {
          player?.updateDuration(e.currentTarget.duration);
        }
        if (ref.current?.hls?.subtitleTracks?.length) {
          player?.updateSubtitles(ref.current.hls.subtitleTracks);
        } else if (ref.current) {
          player?.updateSubtitles(ref.current.element.textTracks);
        }
      },
      [player],
    );
    const handleBgLoad = useCallback(
      (e: SyntheticEvent<HTMLMediaElement>) => {
        if (e.currentTarget.src || e.currentTarget.currentSrc) {
          player?.onBgLoaded();
        }
      },
      [player],
    );

    const handleEnded = useCallback(() => {
      background.current?.element?.pause();
      player?.onEnded();
    }, [player]);

    const img =
      mediaType === "video" || !imgSrc ? null : (
        <HallowImage
          src={imgSrc}
          alt={mediaTitle}
          size={"l"}
          css={imageStyle}
        />
      );

    // we use onLoadedMetadata for duration and onCanPlayThrough for "onLoaded" because
    // the onLoadedData event, normally the most reliable for these checks, is not consistently
    // fired on mobile devices
    const Media = useCallback(
      () =>
        mediaType === "video" ? (
          <Video
            ref={ref as RefObject<HallowVideoElement>}
            css={videoStyle}
            onPlaying={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={player.onElementError}
            onTimeUpdate={handleTimeUpdate}
            onRateChange={handleSpeedChange}
            onVolumeChange={handleVolumeChange}
            onCanPlayThrough={handleLoad}
            onLoadedMetadata={handleMetadataLoad}
            key={`hallowVideoElement_${resetUuid}`}
            hlsUrl={hlsUrl}
            regularUrl={regularUrl}
            policyCookie={policyCookie}
            keyPairIdCookie={keyPairIdCookie}
            signatureCookie={signatureCookie}
          />
        ) : (
          <Audio
            ref={ref}
            key={`hallowAudioElement_${resetUuid}`}
            onPlaying={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={player.onElementError}
            onTimeUpdate={handleTimeUpdate}
            onRateChange={handleSpeedChange}
            onVolumeChange={handleVolumeChange}
            onCanPlayThrough={handleLoad}
            onLoadedMetadata={handleMetadataLoad}
            type={"primary"}
            hlsUrl={hlsUrl}
            regularUrl={regularUrl}
            policyCookie={policyCookie}
            keyPairIdCookie={keyPairIdCookie}
            signatureCookie={signatureCookie}
          />
        ),
      [
        handlePlay,
        handlePause,
        handleTimeUpdate,
        handleSpeedChange,
        handleVolumeChange,
        handleLoad,
        handleMetadataLoad,
        handleEnded,
        hlsUrl,
        regularUrl,
        keyPairIdCookie,
        signatureCookie,
        policyCookie,
        resetUuid,
        mediaType,
      ],
    );

    const BgAudio = useCallback(
      () => (
        <Audio
          ref={background}
          key={"backgroundAudio"}
          type={"background"}
          hlsUrl={bgHlsUrl}
          regularUrl={bgRegularUrl}
          keyPairIdCookie={bgKeyPairIdCookie}
          policyCookie={bgPolicyCookie}
          signatureCookie={bgSignatureCookie}
          onLoadedData={handleBgLoad}
          onError={player.onBgElementError}
        />
      ),
      [
        bgHlsUrl,
        bgRegularUrl,
        bgKeyPairIdCookie,
        bgSignatureCookie,
        bgPolicyCookie,
      ],
    );

    return (
      <Fragment key={"hallowMediaFragment"}>
        <Script
          src={"https://cdn.jsdelivr.net/npm/hls.js@latest/dist/hls.js"}
          strategy={"lazyOnload"}
        ></Script>
        {img}
        <Media key={`hallowMediaWrapper`} />
        <BgAudio key="hallowBackgroundWrapper" />
      </Fragment>
    );
  },
);

MediaElements.displayName = "MediaElements";
